// src/pages/imamMahdi/PracticesAllegiance.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const PracticesAllegiance = () => {
  return (
    <>
      <Helmet>
        <title>Daily Practices to Strengthen Your Allegiance to Imam Mahdi | Ziyarat Guide</title>
        <meta
          name="description"
          content="Discover daily spiritual practices that strengthen your bond and allegiance to Imam Mahdi (aj). Learn about Dua Al-Ahad, Dua Nudba, taqwa, and Qur’an reflection."
        />
        <meta
          property="og:title"
          content="Daily Practices to Strengthen Your Allegiance to Imam Mahdi | Ziyarat Guide"
        />
        <meta
          property="og:description"
          content="Explore simple, meaningful daily actions that deepen your connection with Imam Mahdi (aj) — from reciting Dua Al-Ahad to living with taqwa and compassion."
        />
        <meta
          property="og:image"
          content="https://ziyaratguide.com/wp-content/uploads/2025/08/eight-Daily-practices-to-strengthen-your-allegiance-to-him-.png"
        />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="imam-page">
        <div className="imam-content">
          <h1 className="page-title">
            Daily Practices to Strengthen Your Allegiance to Imam Mahdi
          </h1>

          <div className="content-section">
            <img
              src="https://ziyaratguide.com/wp-content/uploads/2025/08/eight-Daily-practices-to-strengthen-your-allegiance-to-him-.png"
              alt="Daily Practices to Strengthen Allegiance"
              className="imam-image"
            />

            <h2>Keeping the Bond Alive</h2>
            <p>
              In Shia belief, Imam Mahdi (peace be upon him) is not a distant or symbolic figure.
              He is a living Imam, appointed by Allah, who knows the state of his followers and
              intercedes for them.
            </p>
            <p>
              Because of this, Shia Muslims strive to keep their spiritual connection and allegiance
              alive every day—not waiting passively, but preparing actively to be among his loyal
              supporters.
            </p>

            <h2>Simple Daily Practices You Can Start Today</h2>

            <h3>1. Recite Dua Al-Ahad in the Mornings</h3>
            <p>
              This beautiful supplication renews your pledge of loyalty to Imam Mahdi every day. It
              includes powerful lines asking Allah to let you live a life dedicated to the Imam’s
              cause—and if he appears after your death, to be raised again so you can serve him.
            </p>
            <p>
              The hadith promises that whoever recites it for 40 mornings will be counted among the
              helpers of the Imam.
            </p>

            <h3>2. Recite Dua Nudba on Fridays</h3>
            <p>
              Dua Nudba is a moving lament expressing longing for Imam Mahdi’s reappearance. It
              recounts the injustices faced by the Ahlulbayt and calls upon Allah to hasten the
              Imam’s return. It’s traditionally recited on Friday mornings, turning what might be
              just another day into a heartfelt weekly reminder of your love and hope.
            </p>

            <h3>3. Maintain Taqwa (God-Consciousness)</h3>
            <p>
              The Imams taught that the truest sign of allegiance is living by divine ethics. This
              means striving for:
            </p>
            <ul>
              <li>Honesty and integrity in all matters, big and small</li>
              <li>Fairness in dealing with others, avoiding gossip or backbiting</li>
              <li>Compassion and charity, helping the poor and defending the oppressed</li>
            </ul>

            <h3>4. Stay Close to the Qur’an</h3>
            <p>
              Imam Mahdi is described as the living embodiment of the Qur’an’s guidance. Reading and
              reflecting on the Qur’an daily draws you closer to him, since his mission is to
              establish the very truths contained in its verses.
            </p>

            <h3>5. Pray for His Protection & Reappearance</h3>
            <p>Each day, even in simple words, ask Allah:</p>
            <blockquote>
              “O Allah, hasten the reappearance of Your Hujjah, the son of Hasan, and protect him
              from the evil of enemies.”
            </blockquote>
            <p>This keeps your heart constantly mindful of him.</p>

            <h2>Live the Allegiance, Daily</h2>
            <p>
              Awaiting Imam Mahdi (aj) isn’t simply waiting for history to unfold. It’s a daily
              act—praying, reciting duas, living with taqwa and compassion—so when he appears, he
              finds you already walking the path of truth and justice.
            </p>
          </div>
        </div>

        {/* Sidebar Navigation */}
        <aside className="imam-sidebar">
          <h2>Explore More</h2>
          <ul>
            <li>
              <Link to="/imam-mahdi">Who is Imam Mahdi?</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/described-in-quran-hadith">
                Described In Quran & Hadith
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/how-to-talk">How To Talk</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/impact-appearance">Impact of His Appearance</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/misconceptions">Misconceptions</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/practices-allegiance">Practices & Allegiance</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/reflections-stories">Reflections & Stories</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/return">Return</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/signs-reappearance">Signs of Reappearance</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/supporters">Supporters</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/ultimate-reformer">The Ultimate Reformer</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/why-occultation">Why Occultation</Link>
            </li>
          </ul>
        </aside>
      </div>
    </>
  );
};

export default PracticesAllegiance;
